import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  ClipboardList,
  Download,
  FileSpreadsheet,
  MoreVertical,
  Pencil,
  Save,
  Search,
  Target,
  TrendingDown,
  Trophy,
  UserMinus,
  Users,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Menu } from '@/components/ui/Menu';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Table, type Column } from '@/components/ui/Table';
import { gradeForPaper, parseScore } from '@/constants/grading';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadCsv } from '@/lib/download';
import { formatTimeRange } from '@/lib/format';
import { cn, getInitials } from '@/lib/utils';
import type { ExamMarkEntryPayload } from '@/types/exam';
import { examsApi } from '../api/exams.api';
import { EXAM_TYPE_LABEL, MARK_FILTER_OPTIONS } from '../exams/constants';
import {
  EXAM_STATUS_DOT,
  EXAM_STATUS_LABEL,
  EXAM_STATUS_TEXT,
  MARK_STATUS_DOT,
  MARK_STATUS_LABEL,
  MARK_STATUS_TEXT,
  examStatus,
  hasExamEnded,
  markStatus,
} from '../exams/status';
import { useExamClock } from '../exams/useExamClock';
import { examOriginFrom, formatInstituteDate, withExamOrigin } from '../utils';
import { getAvatarColor } from '../utils/avatarColor';

/** One child's line, as it is being edited. Marks stay strings until saved. */
interface MarkDraft {
  marks: string;
  isAbsent: boolean;
  remarks: string;
}

/** One row of the sheet: the child, their draft, and where they sit in the list. */
interface SheetRow {
  uuid: string;
  rollNumber: string | null;
  admissionNumber: string;
  fullName: string;
  draft: MarkDraft;
  serial: number;
}

/** How many children a page of the sheet holds, and what else can be chosen. */
const PAGE_SIZES = [10, 25, 50] as const;

/** An empty line, so a child with no draft yet still has every field. */
const BLANK_DRAFT: MarkDraft = { marks: '', isAbsent: false, remarks: '' };

/** "Priya Ramesh" → "Priya R." — a name that fits under a headline figure. */
function shortName(fullName: string): string {
  const parts = fullName.split(' ').filter(Boolean);
  if (parts.length < 2) return fullName;

  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
}

/**
 * Who holds a headline score. Naming one child when three share the top mark
 * would quietly crown the one who sorted first, so a tie is said as a count.
 */
function holderHint(holders: readonly { fullName: string }[]): string {
  const [first] = holders;
  if (first === undefined) return 'Nothing entered yet';
  return holders.length > 1 ? `${holders.length} students` : shortName(first.fullName);
}

/** A mark as a share of what the paper was out of, to one decimal. */
function sharePercent(marks: number | null, outOf: number): number | null {
  return marks === null || outOf <= 0 ? null : Math.round((marks / outOf) * 1000) / 10;
}

/**
 * The marks sheet for one paper.
 *
 * A table of the whole class with one Save, because that is what a marks sheet
 * *is* — the teacher works down a pile of papers and commits the lot. Saving a
 * mark at a time would leave a sheet that is half entered with nobody able to
 * say which half, which is the same reason the attendance register saves whole.
 *
 * The grade beside each mark, and every figure across the top, are worked out as
 * you type — so a teacher sees what a mark means before committing to it, and
 * the summary never lags the boxes it is describing.
 *
 * **A finished sheet opens read-only.** Once every child is accounted for the
 * paper is a record rather than a form, and it takes a deliberate press of Edit
 * to change one — while a sheet with marks still to enter opens ready to type,
 * because that is what the teacher came here to do.
 */
export function TeacherExamMarksPage() {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  /** The exams tab that sent them here, so every way back reopens it. */
  const [searchParams] = useSearchParams();
  const papersPath = withExamOrigin('/teacher/marks', examOriginFrom(searchParams));

  const { data, isLoading, error, refetch } = useApiQuery(
    () => examsApi.get(examId ?? ''),
    [examId],
  );

  const [search, setSearch] = useState('');
  const [rowFilter, setRowFilter] = useState('');
  const [drafts, setDrafts] = useState<Record<string, MarkDraft>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [isSuspending, setIsSuspending] = useState(false);
  const [isConfirmingSuspend, setIsConfirmingSuspend] = useState(false);

  const exam = data?.exam ?? null;
  const students = useMemo(() => data?.students ?? [], [data]);
  const canEnter = Boolean(data?.canEnterMarks);

  // Re-read only when this paper ends: once it is over there is no later end to
  // wait for, so the standing cannot change under someone entering marks.
  const now = useExamClock(exam ? [exam] : []);
  const hasEnded = exam !== null && hasExamEnded(exam, now);
  /** Set while the open paper is still being written, so its end is noticed. */
  const wasRunning = useRef(false);

  // The server decides `canEnterMarks`, so the sheet asks again the moment the
  // paper ends rather than unlocking itself. Only on that transition: a paper
  // already over when it loaded is read-only for a reason a re-read won't change.
  useEffect(() => {
    if (exam === null) return;
    if (!hasEnded) {
      wasRunning.current = true;
      return;
    }
    if (wasRunning.current) {
      wasRunning.current = false;
      refetch();
    }
  }, [exam, hasEnded, refetch]);

  // Seeded from what is stored, so an edit starts from the current mark rather
  // than from an empty box that would read as "not entered".
  useEffect(() => {
    setDrafts(
      Object.fromEntries(
        students.map((student) => [
          student.uuid,
          {
            marks: student.marksObtained === null ? '' : String(student.marksObtained),
            isAbsent: student.isAbsent,
            remarks: student.remarks ?? '',
          },
        ]),
      ),
    );
    setSelected(new Set());
  }, [students]);

  /**
   * A sheet with work left in it opens ready to type; a finished one opens as a
   * record. Re-read after every save, so committing the last mark closes the
   * form rather than leaving a finished sheet sitting open and editable.
   */
  useEffect(() => {
    if (!data) return;
    setIsEditing(
      data.canEnterMarks && data.exam.enteredCount < data.exam.totalStudents,
    );
  }, [data]);

  function setDraft(uuid: string, patch: Partial<MarkDraft>) {
    setDrafts((current) => ({
      ...current,
      [uuid]: { ...BLANK_DRAFT, ...current[uuid], ...patch },
    }));
  }

  const rows = useMemo<SheetRow[]>(() => {
    const needle = search.trim().toLowerCase();

    return students
      .filter((student) => {
        const matchesSearch =
          !needle ||
          student.fullName.toLowerCase().includes(needle) ||
          (student.rollNumber ?? '').toLowerCase().includes(needle) ||
          student.admissionNumber.toLowerCase().includes(needle);
        if (!matchesSearch) return false;

        // Filters read the *draft*, not the saved row, so a child just marked
        // stops matching "Not entered" without waiting for a save.
        const draft = drafts[student.uuid];
        const typed = draft ? parseScore(draft.marks) : student.marksObtained;
        const absent = draft ? draft.isAbsent : student.isAbsent;

        switch (rowFilter) {
          case 'PENDING':
            return typed === null && !absent;
          case 'ENTERED':
            return typed !== null;
          case 'ABSENT':
            return absent;
          case 'FAILED':
            return typed !== null && exam !== null && typed < exam.passMarks;
          default:
            return true;
        }
      })
      .map((student, index) => ({
        uuid: student.uuid,
        rollNumber: student.rollNumber,
        admissionNumber: student.admissionNumber,
        fullName: student.fullName,
        draft: drafts[student.uuid] ?? BLANK_DRAFT,
        serial: index + 1,
      }));
  }, [students, drafts, search, rowFilter, exam]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  // Clamped rather than stored, so narrowing the sheet from page four cannot
  // leave the table on a page that no longer exists.
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Narrowing the sheet starts the reader at the top again, and drops whatever
  // was ticked: a bulk action must never reach a child who has scrolled out of
  // sight behind a filter.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [search, rowFilter, pageSize]);

  /** The search box and the row filter, the two things that can hide children. */
  const isFiltered = search.trim() !== '' || rowFilter !== '';

  function clearFilters() {
    setSearch('');
    setRowFilter('');
  }

  /**
   * The sheet as it currently stands, including unsaved edits — what the tiles
   * across the top show, so they never lag the boxes.
   */
  const live = useMemo(() => {
    if (!exam) return null;
    const scored: { marks: number; fullName: string }[] = [];
    let absent = 0;
    let entered = 0;

    for (const student of students) {
      const draft = drafts[student.uuid];
      const isAbsent = draft ? draft.isAbsent : student.isAbsent;
      const marks = draft ? parseScore(draft.marks) : student.marksObtained;

      if (isAbsent) {
        absent += 1;
        entered += 1;
        continue;
      }
      if (marks !== null) {
        scored.push({ marks, fullName: student.fullName });
        entered += 1;
      }
    }

    const sorted = [...scored].sort((a, b) => b.marks - a.marks);

    return {
      entered,
      absent,
      // Absences are left out of the average — a child who sat no paper has no
      // score, and counting them as zero would drag the class down.
      average: scored.length
        ? Math.round((scored.reduce((sum, s) => sum + s.marks, 0) / scored.length) * 10) / 10
        : null,
      passed: scored.filter((s) => s.marks >= exam.passMarks).length,
      top: sorted[0] ?? null,
      bottom: sorted.length > 0 ? sorted[sorted.length - 1] : null,
      topHolders: sorted.filter((s) => s.marks === sorted[0]?.marks),
      bottomHolders: sorted.filter((s) => s.marks === sorted[sorted.length - 1]?.marks),
    };
  }, [students, drafts, exam]);

  /** Anything typed that the server has not been told about yet. */
  const isDirty = students.some((student) => {
    const draft = drafts[student.uuid];
    if (!draft) return false;

    return (
      parseScore(draft.marks) !== student.marksObtained ||
      draft.isAbsent !== student.isAbsent ||
      draft.remarks !== (student.remarks ?? '')
    );
  });

  const overMax = students.filter((student) => {
    const typed = parseScore(drafts[student.uuid]?.marks ?? '');

    return exam !== null && typed !== null && typed > exam.maxMarks;
  });

  /** The rows a bulk action would touch: what is ticked, on this page or not. */
  const pageSelection = pageRows.filter((row) => selected.has(row.uuid));

  function toggleSelected(uuid: string, isSelected: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (isSelected) next.add(uuid);
      else next.delete(uuid);

      return next;
    });
  }

  function togglePage(isSelected: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      for (const row of pageRows) {
        if (isSelected) next.add(row.uuid);
        else next.delete(row.uuid);
      }

      return next;
    });
  }

  /** Marks everybody ticked as absent in one go — an exam day with five empty desks. */
  function markSelectedAbsent() {
    setDrafts((current) => {
      const next = { ...current };
      for (const uuid of selected) {
        // Clearing the box as it is ticked keeps the two facts from
        // contradicting each other.
        next[uuid] = { ...BLANK_DRAFT, ...next[uuid], isAbsent: true, marks: '' };
      }

      return next;
    });
    setSelected(new Set());
  }

  async function saveSheet() {
    if (!exam) return;
    if (overMax.length > 0) {
      toast.error(
        'Some marks are above the total',
        `This paper is out of ${exam.maxMarks}. Fix ${overMax.length === 1 ? 'that mark' : 'those marks'} first.`,
      );
      return;
    }

    setIsSaving(true);
    try {
      const entries: ExamMarkEntryPayload[] = students.map((student) => {
        const draft = drafts[student.uuid] ?? BLANK_DRAFT;

        return {
          studentUuid: student.uuid,
          // Absent wins: a child who sat no paper has no mark, whatever is left
          // in the box from before the tick.
          marksObtained: draft.isAbsent ? null : parseScore(draft.marks),
          isAbsent: draft.isAbsent,
          remarks: draft.remarks.trim() || undefined,
        };
      });

      const saved = await examsApi.saveMarks(exam.uuid, entries);
      toast.success(
        'Marks saved',
        `${saved.exam.enteredCount} of ${saved.exam.totalStudents} entered${
          saved.exam.averageMarks !== null ? ` · average ${saved.exam.averageMarks}` : ''
        }`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        'Could not save the sheet',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function suspendPaper() {
    if (!exam) return;
    setIsSuspending(true);

    try {
      await examsApi.remove(exam.uuid);
      toast.success(
        `${exam.name} has been withdrawn`,
        'The marks recorded against it are kept.',
      );
      navigate(papersPath);
    } catch (caught) {
      setIsConfirmingSuspend(false);
      toast.error(
        `Could not withdraw ${exam.name}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsSuspending(false);
    }
  }

  /**
   * Saves the sheet as it stands on screen, unsaved edits and all.
   *
   * `rows` is already narrowed by the search box and the filter, so the file
   * matches the table the teacher is looking at rather than quietly holding
   * more children than they can see.
   */
  function exportSheet() {
    if (!exam) return;

    downloadCsv(
      `marks-${exam.name.replace(/\s+/g, '-')}-${exam.classSection.displayName.replace(/\s+/g, '-')}-${exam.examDate}.csv`,
      [
        ['Roll No', 'Admission No', 'Student Name', 'Status', `Marks (out of ${exam.maxMarks})`, 'Percentage', 'Grade', 'Remarks'],
        ...rows.map((row) => {
          const typed = parseScore(row.draft.marks);
          const status = markStatus({ marks: typed, isAbsent: row.draft.isAbsent });
          const share = row.draft.isAbsent ? null : sharePercent(typed, exam.maxMarks);
          const band = row.draft.isAbsent
            ? null
            : gradeForPaper(typed, exam.maxMarks, exam.passMarks);

          return [
            row.rollNumber,
            row.admissionNumber,
            row.fullName,
            MARK_STATUS_LABEL[status],
            row.draft.isAbsent ? '' : (typed ?? ''),
            share === null ? '' : `${share}%`,
            band?.grade ?? '',
            row.draft.remarks,
          ];
        }),
      ],
    );
  }

  const columns: Column<SheetRow>[] = [
    // Only while the sheet is open for editing: a tick box with nothing it can
    // do to a read-only record is a control that lies about being one.
    ...(isEditing
      ? [
          {
            key: 'select',
            header: (
              <Checkbox
                label="Select every student on this page"
                hideLabel
                checked={pageRows.length > 0 && pageSelection.length === pageRows.length}
                indeterminate={pageSelection.length > 0 && pageSelection.length < pageRows.length}
                onChange={(event) => togglePage(event.target.checked)}
              />
            ),
            align: 'center' as const,
            className: 'w-12',
            headerClassName: 'w-12',
            render: (row: SheetRow) => (
              <Checkbox
                label={`Select ${row.fullName}`}
                hideLabel
                checked={selected.has(row.uuid)}
                onChange={(event) => toggleSelected(row.uuid, event.target.checked)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'serial',
      header: '#',
      align: 'center',
      className: 'w-12',
      headerClassName: 'w-12',
      render: (row) => <span className="text-xs font-semibold text-ink-3">{row.serial}</span>,
    },
    {
      key: 'roll',
      header: 'Roll No',
      align: 'center',
      render: (row) => (
        <span className="font-mono text-sm font-medium text-ink-2">{row.rollNumber ?? '—'}</span>
      ),
    },
    {
      key: 'name',
      header: 'Student Name',
      render: (row) => {
        const [first = '', ...rest] = row.fullName.split(' ').filter(Boolean);

        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className={cn(
                'grid h-8 w-8 shrink-0 place-items-center rounded-full border text-xs font-bold shadow-2xs',
                getAvatarColor(row.uuid),
              )}
              aria-hidden
            >
              {getInitials(first, rest.at(-1) ?? first)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-ink" title={row.fullName}>
                {row.fullName}
              </p>
              <p className="truncate text-2xs font-semibold text-ink-3">{row.admissionNumber}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const status = markStatus({ marks: parseScore(row.draft.marks), isAbsent: row.draft.isAbsent });

        return (
          <span
            className={cn(
              'flex items-center gap-1.5 whitespace-nowrap text-sm font-bold',
              MARK_STATUS_TEXT[status],
            )}
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', MARK_STATUS_DOT[status])} aria-hidden />
            {MARK_STATUS_LABEL[status]}
          </span>
        );
      },
    },
    {
      key: 'marks',
      header: exam ? `Marks (Out of ${exam.maxMarks})` : 'Marks',
      align: 'center',
      render: (row) => {
        const typed = parseScore(row.draft.marks);
        const isOver = exam !== null && typed !== null && typed > exam.maxMarks;

        return (
          <div className="mx-auto w-24">
            <Input
              label={`Marks for ${row.fullName}`}
              hideLabel
              inputMode="numeric"
              placeholder={row.draft.isAbsent ? 'Absent' : '—'}
              disabled={!isEditing || row.draft.isAbsent}
              error={isOver ? 'Too high' : undefined}
              value={row.draft.isAbsent ? '' : row.draft.marks}
              onChange={(event) => setDraft(row.uuid, { marks: event.target.value })}
              className="h-9 text-center text-sm font-bold"
            />
          </div>
        );
      },
    },
    {
      key: 'percent',
      header: 'Percentage',
      align: 'center',
      hideOnMobile: true,
      render: (row) => {
        if (row.draft.isAbsent) return <span className="text-ink-3">—</span>;
        const share = exam ? sharePercent(parseScore(row.draft.marks), exam.maxMarks) : null;
        if (share === null) return <span className="text-ink-3">—</span>;

        return (
          <span
            className={cn(
              'text-sm font-bold tabular-nums',
              exam !== null && (parseScore(row.draft.marks) ?? 0) >= exam.passMarks
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-rose-600 dark:text-rose-400',
            )}
          >
            {share.toFixed(1)}%
          </span>
        );
      },
    },
    {
      key: 'grade',
      header: 'Grade',
      align: 'center',
      render: (row) => {
        if (row.draft.isAbsent) return <Badge tone="warning">Absent</Badge>;

        const typed = parseScore(row.draft.marks);
        const isOver = exam !== null && typed !== null && typed > exam.maxMarks;
        const band = exam && !isOver ? gradeForPaper(typed, exam.maxMarks, exam.passMarks) : null;
        if (!band) return <span className="text-ink-3">—</span>;

        const failed = exam !== null && typed !== null && typed < exam.passMarks;

        return <Badge tone={failed ? 'critical' : band.tone}>{band.grade}</Badge>;
      },
    },
    {
      key: 'remarks',
      header: 'Remarks',
      render: (row) => (
        <Input
          label={`Remark for ${row.fullName}`}
          hideLabel
          placeholder={isEditing ? 'Optional' : '—'}
          disabled={!isEditing}
          value={row.draft.remarks}
          onChange={(event) => setDraft(row.uuid, { remarks: event.target.value })}
          className="h-9 min-w-[10rem] text-sm"
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'center',
      render: (row) => (
        <Menu
          label={`More actions for ${row.fullName}`}
          trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
          triggerClassName="h-8 w-8 border border-line bg-surface text-ink-2 hover:border-accent hover:text-accent"
          disabled={!isEditing}
          items={[
            {
              key: 'absent',
              label: row.draft.isAbsent ? 'Mark as present' : 'Mark as absent',
              description: row.draft.isAbsent
                ? 'Lets a mark be entered again.'
                : 'Clears the mark — an absence is not a zero.',
              icon: <UserMinus className="h-4 w-4" />,
              onSelect: () =>
                setDraft(row.uuid, {
                  isAbsent: !row.draft.isAbsent,
                  ...(!row.draft.isAbsent && { marks: '' }),
                }),
            },
            {
              key: 'clear',
              label: 'Clear this line',
              description: 'Empties the mark and the remark. Saved once you press Save.',
              icon: <TrendingDown className="h-4 w-4" />,
              tone: 'danger',
              disabled: row.draft.marks === '' && row.draft.remarks === '' && !row.draft.isAbsent,
              onSelect: () => setDraft(row.uuid, { ...BLANK_DRAFT }),
            },
          ]}
        />
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex flex-col gap-5">
        <Breadcrumb items={[{ label: 'Exam Marks', to: papersPath }, { label: 'Paper' }]} />
        <QueryError title="Could not load this paper" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (isLoading || !exam || !live) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-56 rounded-field" />
        <Skeleton className="h-24 w-full rounded-card" />
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3.5 xl:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <Skeleton key={key} className="h-20 w-full rounded-card" />
          ))}
        </div>
        <Skeleton className="h-96 w-full rounded-card" />
      </div>
    );
  }

  const paperStatus = examStatus(exam, now);
  const averageShare = sharePercent(live.average, exam.maxMarks);

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumb
        items={[{ label: 'Exam Marks', to: papersPath }, { label: exam.name }]}
      />

      {/* --- 1. THE PAPER ----------------------------------------------------- */}
      <header className="card flex flex-col gap-3.5 rounded-[16px] border border-line bg-surface p-4 shadow-2xs lg:flex-row lg:items-center lg:justify-between lg:p-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px] border border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400 sm:h-12 sm:w-12"
            aria-hidden
          >
            <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="truncate text-xl font-bold tracking-tight text-ink sm:text-2xl">
                {exam.name}
              </h1>
              <span
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap text-xs font-bold',
                  EXAM_STATUS_TEXT[paperStatus],
                )}
              >
                <span
                  className={cn('h-2 w-2 shrink-0 rounded-full', EXAM_STATUS_DOT[paperStatus])}
                  aria-hidden
                />
                {EXAM_STATUS_LABEL[paperStatus]}
              </span>
            </div>

            <p className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs font-semibold text-ink-3 sm:text-sm">
              <span>{exam.classSection.displayName}</span>
              <span aria-hidden className="text-line">|</span>
              <span>{EXAM_TYPE_LABEL[exam.examType]}</span>
              <span aria-hidden className="text-line">|</span>
              <span>{exam.subject.name}</span>
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={exportSheet}
            disabled={rows.length === 0}
            className="flex-1 sm:flex-initial"
          >
            Download Marks
          </Button>

          <Menu
            label={`More actions for ${exam.name}`}
            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
            triggerClassName="h-9 w-9 border border-line bg-surface text-ink-2 hover:border-accent hover:text-accent shrink-0"
            items={[
              {
                key: 'papers',
                label: 'Back to all papers',
                icon: <FileSpreadsheet className="h-4 w-4" />,
                onSelect: () => navigate(papersPath),
              },
              // Only whoever set a class test may withdraw it; an institute
              // exam is the office's. Marking it is a separate right.
              ...(exam.canEdit
                ? [
                    {
                      key: 'suspend',
                      label: 'Suspend this paper',
                      description: 'Stops it appearing for the class. Marks already entered are kept.',
                      icon: <ClipboardList className="h-4 w-4" />,
                      tone: 'danger' as const,
                      onSelect: () => setIsConfirmingSuspend(true),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </header>

      {/* --- 2. HEADLINE FIGURES, FOLLOWING THE BOXES ------------------------- */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3.5 xl:grid-cols-6">
        <MetricTile
          tone="sky"
          icon={<CalendarDays className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Exam Date"
          value={<span className="text-base sm:text-lg">{formatInstituteDate(exam.examDate)}</span>}
          hint={formatTimeRange(exam.startTime, exam.endTime, 'Time not recorded')}
        />
        <MetricTile
          tone="teal"
          icon={<Target className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Out of"
          value={exam.maxMarks}
          hint={`Pass at ${exam.passMarks}`}
        />
        <MetricTile
          tone="violet"
          icon={<Users className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Students"
          value={exam.totalStudents}
          hint={`${live.entered} accounted for`}
        />
        <MetricTile
          tone="blue"
          icon={<BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Average Score"
          value={live.average === null ? '—' : live.average}
          hint={
            averageShare === null
              ? 'Nothing entered yet'
              : `${averageShare}% · ${live.passed} passed`
          }
        />
        <MetricTile
          tone="amber"
          icon={<Trophy className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Highest Score"
          value={live.top === null ? '—' : live.top.marks}
          hint={holderHint(live.topHolders)}
        />
        <MetricTile
          tone="rose"
          icon={<TrendingDown className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Lowest Score"
          value={live.bottom === null ? '—' : live.bottom.marks}
          hint={holderHint(live.bottomHolders)}
        />
      </div>

      {!canEnter && (
        // The server decides `canEnterMarks`; the standing only picks the reason,
        // so a teacher waiting on a paper is not told their role is the problem.
        <Alert tone="info" title={paperStatus === 'SCHEDULED' ? 'Not over yet' : 'Read only'}>
          {paperStatus === 'SCHEDULED'
            ? `Marks can be entered once this paper is over${exam.endTime ? ` — after ${exam.endTime} on ${formatInstituteDate(exam.examDate)}` : ''}.`
            : 'Your role lets you see this paper but not enter marks against it.'}
        </Alert>
      )}

      {/* --- 3. THE SHEET ------------------------------------------------------ */}
      <section className="card overflow-hidden rounded-[16px] border border-line bg-surface shadow-2xs">
        <div className="flex flex-col gap-3.5 border-b border-line p-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight text-ink">Marks Entry</h2>
            <p className="mt-0.5 text-xs font-medium text-ink-3">
              {isEditing
                ? 'Grades and percentages follow the boxes as you type. Nothing is stored until you press Save.'
                : 'This sheet is complete. Press Edit to change a mark.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 w-full xl:w-auto">
            <div className="w-full sm:w-64">
              <Input
                label="Search students"
                hideLabel
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by student name or roll no..."
                leftIcon={<Search className="h-3.5 w-3.5" />}
                className="h-9 w-full text-[13px]"
              />
            </div>

            <div className="w-full sm:w-44">
              <Select
                label="Show which students"
                hideLabel
                options={MARK_FILTER_OPTIONS}
                value={rowFilter}
                onChange={(event) => setRowFilter(event.target.value)}
                className="h-9 text-[13px] font-semibold"
              />
            </div>

            <ClearFiltersButton isActive={isFiltered} onClear={clearFilters} />

            {canEnter && (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="secondary"
                  leftIcon={<Pencil className="h-4 w-4" />}
                  className="h-9 flex-1 sm:flex-initial text-[13px] font-semibold"
                  aria-pressed={isEditing}
                  onClick={() => {
                    // Leaving edit mode with unsaved work would throw it away
                    // silently, so the button simply stays put and says why.
                    if (isEditing && isDirty) {
                      toast.info('Unsaved marks on this sheet', 'Save them, or reload to discard.');
                      return;
                    }
                    setIsEditing((editing) => !editing);
                  }}
                >
                  {isEditing ? 'Editing' : 'Edit'}
                </Button>

                <Button
                  leftIcon={<Save className="h-4 w-4" />}
                  className="h-9 flex-1 sm:flex-initial text-[13px] font-semibold"
                  isLoading={isSaving}
                  disabled={!isEditing || !isDirty || overMax.length > 0}
                  onClick={saveSheet}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* The bulk bar only exists while something is ticked, so it never sits
            there as an empty strip of buttons with nothing to act on. */}
        {isEditing && selected.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-line bg-surface-2 px-4 py-2.5">
            <p className="text-sm font-bold text-ink">
              {selected.size} {selected.size === 1 ? 'student' : 'students'} selected
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<UserMinus className="h-3.5 w-3.5" />}
                onClick={markSelectedAbsent}
              >
                Mark absent
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Clear selection
              </Button>
            </div>
          </div>
        )}

        {overMax.length > 0 && (
          <div className="border-b border-line px-4 py-3">
            <Alert tone="critical" title="Some marks are above the total">
              {overMax.length === 1 ? 'One mark is' : `${overMax.length} marks are`} above{' '}
              {exam.maxMarks}. Fix {overMax.length === 1 ? 'it' : 'them'} before saving.
            </Alert>
          </div>
        )}

        <Table
          variant="plain"
          columns={columns}
          rows={pageRows}
          rowKey={(row) => row.uuid}
          empty={
            <EmptyState
              icon={<Users className="h-5 w-5" />}
              title={students.length === 0 ? 'Nobody on this roster yet' : 'No children match'}
              description={
                students.length === 0
                  ? 'This section has no active students, so there is nobody to mark. Admissions fill the roster.'
                  : 'Nothing matches that search or filter. Clear them to see the whole class.'
              }
            />
          }
        />

        {rows.length > 0 && (
          <Pagination
            meta={{ page: currentPage, limit: pageSize, total: rows.length, totalPages }}
            onPageChange={setPage}
            itemLabel={rows.length === 1 ? 'student' : 'students'}
            showPageNumbers
            limitOptions={PAGE_SIZES}
            onLimitChange={setPageSize}
          />
        )}
      </section>

      <ConfirmDialog
        isOpen={isConfirmingSuspend}
        tone="danger"
        title={`Suspend ${exam.name}?`}
        description={`Nothing is deleted. It stops appearing for ${exam.classSection.displayName}, and the ${exam.enteredCount} ${exam.enteredCount === 1 ? 'mark' : 'marks'} already entered are kept.`}
        confirmLabel="Suspend paper"
        isLoading={isSuspending}
        onConfirm={suspendPaper}
        onCancel={() => setIsConfirmingSuspend(false)}
      />
    </div>
  );
}
